import Spinner from './Spinner';
import { Link, ThemeProvider, Typography, Button, AppHeaderBackButton } from '@ace-clublabs/shared-component-library';
import { Header } from './Header';
import { useEffect, useState } from 'react';
import { useGlobalState } from '../shared/state';
import { useNavigate } from 'react-router-dom';
import { Pages } from '../shared/pages';
import { UtagHelper, UtagParam } from '../utils/utagHelper';
import { PolicyType, ICreateManifestRequest, ErrorType } from '../shared/types';
import { ProductType } from '@ace-clublabs/payment-types';
import priceTag from '../assets/price_tag.svg';
import { constants } from '@ace-clublabs/payment-types';
import { sources } from '../api/sources';
import { Methods } from '@ace-clublabs/payments-helper';

export const AutoPayConsent: React.FC = () => {
  return <AutoPayConsentView />;
};

export const AutoPayConsentView: React.FC = () => {
  const [isLoading, setLoading] = useState(false);
  const [selectedProduct] = useGlobalState('selectedProduct') as any;
  const [billPlan] = useGlobalState('billPlan');
  const [, setNewEnroll] = useGlobalState('newEnroll');
  const [sessionId] = useGlobalState('sessionId');
  const [clubCode] = useGlobalState('clubCode');
  const [discount] = useGlobalState('discount');
  const [brokerRegion] = useGlobalState('brokerRegion');
  const [memcid] = useGlobalState('memcid');
  const [referrer] = useGlobalState('referrer');
  const [, setConfirmationVisited] = useGlobalState('confirmationVisited');
  const [, setConfirmationDetails] = useGlobalState('confirmationDetails');
  const [, setTransactionCheckMade] = useGlobalState('transactionCheckMade');
  const { policyType, autoPay, minDue } = selectedProduct;

  const hasMinDue = minDue ? minDue > 0 : false;

  const productType = selectedProduct
    ? policyType === PolicyType.Membership
      ? ProductType.Membership
      : ProductType.Insurance
    : undefined;

  const isMembership = policyType === PolicyType.Membership;

  const isMssEnrollShortcut =
    isMembership && !autoPay?.enrolled && referrer?.toLowerCase().includes('membership/selfservice');

  const navigate = useNavigate();
  const renderAlert = () => {
    const discountEligible = discount > 0;
    if (!discountEligible) return <></>;
    return (
      <div id="discount-message" className="membership-discount">
        <div className="icon-container">
          <img id="info-tag-img" alt="info-tag-icon" src={priceTag} />
        </div>
        <Typography variant="b2Regular">
          <span>
            <strong>Save ${discount?.toFixed(2)} today</strong> with your Auto Pay enrollment.
          </span>
        </Typography>
      </div>
    );
  };

  const displayFinanceDisclaimer: boolean = clubCode === '004' && productType === ProductType.Insurance;

  useEffect(() => {
    const baseTags = {
      ...UtagHelper.CommonTaggingValues,
      clubcode: clubCode,
      sessionid: sessionId,
      mem_billplan: billPlan,
      ...(selectedProduct && { flow: productType === ProductType.Membership ? 'membership' : 'insurance' }),
    } as UtagParam;

    UtagHelper.tag({
      ...baseTags,
      page: UtagHelper.Pages.enrollment,
    });
  }, []);

  useEffect(() => {
    const resetStateOnBFCache = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoading(false);
      }
    };
    window.addEventListener('pageshow', resetStateOnBFCache);
    return () => {
      window.removeEventListener('pageshow', resetStateOnBFCache);
    };
  }, []);

  const sendManifestRequest = async () => {
    let manifestRequest: ICreateManifestRequest = {
      productId: selectedProduct.productId,
      productType:
        selectedProduct.policyType === PolicyType.Membership ? ProductType.Membership : ProductType.Insurance,
      brokerRegion,
      newEnroll: true,
      tags: {
        ...UtagHelper.CommonTaggingValues,
        clubcode: clubCode,
        page: UtagHelper.Pages.payment,
        sessionid: sessionId,
        mem_billplan: billPlan,
        ...(selectedProduct && { flow: productType === ProductType.Membership ? 'membership' : 'insurance' }),
      },
    };
    if (memcid) manifestRequest = { ...manifestRequest, memcid: memcid };

    try {
      setLoading(true);
      setConfirmationVisited(false);
      setConfirmationDetails({} as any);
      setTransactionCheckMade(false);
      const { manifestId } = await sources.createManifest(manifestRequest);
      const url = `https://${window.location.hostname}/payments/${manifestId}`;
      window.location.assign(url);
    } catch (e: any) {
      navigate(Pages.Error, { state: { type: ErrorType.SYSTEM_ERROR } });
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <div className="container">
        <Header />
        <AppHeaderBackButton
          id="back-button"
          onClick={() =>
            hasMinDue
              ? navigate(-1)
              : isMssEnrollShortcut
              ? window.location.assign(referrer)
              : navigate(Pages.ManageAutoPay)
          }
        />
        <ThemeProvider>
          <Typography variant="h5" component="h1" sx={{ fontWeight: '500', marginBottom: '5px' }}>
            Confirm your Auto Pay enrollment
          </Typography>
          <Typography variant="b1Regular" sx={{ display: 'block', marginBottom: '16px' }}>
            With Auto Pay, you will never miss your payment due date. You can discontinue Auto Pay at any time by
            calling us.
          </Typography>
          {productType === ProductType.Membership && (
            <Typography variant="b1Regular">
              Your membership will renew automatically unless you cancel your Auto Pay before your membership expires.
            </Typography>
          )}
          {renderAlert()}
          <Button
            variant="contained"
            label="Enroll and continue"
            fullWidth
            onClick={() => {
              setNewEnroll(selectedProduct.productId);
              sendManifestRequest();
            }}
            sx={{ marginTop: '10px' }}
          ></Button>
          <Button
            variant="outlined"
            label="Cancel"
            fullWidth
            id="consent-cancel-button"
            onClick={() => {
              navigate(Pages.ManageAutoPay);
            }}
            sx={{ marginBottom: '10px' }}
          ></Button>
          <Typography variant="b2Regular" className="terms-and-conditions">
            By clicking “Enroll and continue”, I hereby enroll in Auto Pay and have read and agree to the{' '}
            <Link
              data-id="terms-conditions-link"
              id="terms-conditions-link"
              variant="b2Regular"
              href={Methods.getTermsUrl()}
              target="_blank"
              sx={{ verticalAlign: 'baseline' }}
            >
              Terms & Conditions
            </Link>
            .
          </Typography>
          {displayFinanceDisclaimer && (
            <>
              <Typography
                component="h2"
                variant="b2Regular"
                sx={{ mt: 2, color: '#6C7175', marginTop: '8px' }}
                id="finance-disclaimer-title"
              >
                {constants.disclaimer_finance_fee_title}
              </Typography>
              <Typography
                component="h3"
                variant="b2Regular"
                sx={{ color: '#6C7175', marginTop: '8px' }}
                id="finance-disclaimer"
              >
                {constants.disclaimer_finance_fee}
              </Typography>
            </>
          )}
        </ThemeProvider>
      </div>
    </>
  );
};
